import { useEffect, useState } from "react";
import { api, type Dashboard } from "../lib/api.js";
import { Mostarda, type EstadoMascote } from "../components/Mostarda.js";
import { AnelMacro } from "../components/AnelMacro.js";

const FOCO: Record<string, string> = {
  body_composition: "Composição corporal",
  performance: "Performance",
  clinical: "Saúde clínica",
  consistency: "Consistência",
};

const BANDA: Record<string, { texto: string; cor: string }> = {
  baixo: { texto: "Recuperação baixa", cor: "text-alerta" },
  moderado: { texto: "Recuperação moderada", cor: "text-suave" },
  bom: { texto: "Recuperação boa", cor: "text-bom" },
  otimo: { texto: "Recuperação ótima", cor: "text-bom" },
};

export function Painel({
  aoAbrirFoto,
  aoAbrirPeso,
  aoAbrirCheckIn,
  aoAbrirImportacao,
  aoAbrirPareamento,
}: {
  aoAbrirFoto: () => void;
  aoAbrirPeso: () => void;
  aoAbrirCheckIn: () => void;
  aoAbrirImportacao: () => void;
  aoAbrirPareamento: () => void;
}) {
  const [dados, setDados] = useState<Dashboard | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.get<Dashboard>("/dashboard").then(setDados).catch((e) => setErro(String(e.message ?? e)));
  }, []);

  if (erro) return <p className="p-5 text-alerta">{erro}</p>;
  if (!dados) return <p className="p-5 text-suave">Carregando…</p>;

  const { readiness, tdee, progress, weight } = dados;

  // A faixa de incerteza só aparece quando o dia tem refeição estimada por foto.
  const incerto = dados.intakeConfidence !== null && dados.intakeConfidence < 0.9;
  const erroRelativo = incerto ? 0.05 + (1 - (dados.intakeConfidence ?? 1)) * 0.7 : 0;

  return (
    <main className="mx-auto flex max-w-md flex-col gap-4 px-4 pb-28 pt-6">
      <header className="flex items-center gap-3">
        <Mostarda estado={dados.mascotState as EstadoMascote} tamanho={76} />
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold">Oi, {dados.user.name}</h1>
          <p className="text-sm text-suave">
            {dados.cycle ? `${dados.cycle.name} · ${FOCO[dados.cycle.focus] ?? dados.cycle.focus}` : "Sem ciclo ativo"}
          </p>
        </div>
      </header>

      {/* Readiness: UMA frase. É o que Whoop e Oura acertaram — número sozinho
          não diz o que fazer hoje. */}
      <section className="cartao flex flex-col gap-1">
        <div className="flex items-baseline justify-between">
          <span className="rotulo">Hoje</span>
          {readiness.score !== null && (
            <span className={`text-sm font-semibold ${BANDA[readiness.band ?? "moderado"]?.cor ?? ""}`}>
              {readiness.score}/100 · {BANDA[readiness.band ?? "moderado"]?.texto}
            </span>
          )}
        </div>
        <p className="text-[15px] leading-snug">{readiness.headline}</p>
        {readiness.note && <p className="text-xs text-suave">{readiness.note}</p>}

        {!dados.checkin.feitoHoje && (
          <button
            className="mt-1 rounded-xl2 border border-mostarda/40 bg-mostarda/10 px-3 py-2 text-left text-sm"
            onClick={aoAbrirCheckIn}
          >
            <span className="font-semibold">Faz o check-in de hoje</span>
            <span className="block text-xs text-suave">
              Energia e dor, dois toques. Sem HRV, é o que mais move teu score.
            </span>
          </button>
        )}
      </section>

      {progress && dados.macros ? (
        <section className="cartao">
          <div className="mb-3 flex items-baseline justify-between">
            <span className="rotulo">Macros do dia</span>
            {incerto && (
              <span className="text-xs text-suave">
                estimado por foto · faixa ±{Math.round(erroRelativo * 100)}%
              </span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-1">
            {progress.map((p) => (
              <AnelMacro
                key={p.key}
                rotulo={{ kcal: "kcal", proteinG: "prot", carbG: "carb", fatG: "gord" }[p.key]}
                consumido={p.consumed}
                alvo={p.target}
                unidade={p.key === "kcal" ? "" : "g"}
                cor={
                  {
                    kcal: "rgb(var(--c-mostarda))",
                    proteinG: "rgb(var(--c-proteina))",
                    carbG: "rgb(var(--c-carbo))",
                    fatG: "rgb(var(--c-gordura))",
                  }[p.key]
                }
                faixa={
                  incerto
                    ? { baixo: p.consumed * (1 - erroRelativo), alto: p.consumed * (1 + erroRelativo) }
                    : null
                }
              />
            ))}
          </div>
        </section>
      ) : (
        <section className="cartao text-sm text-suave">
          Ainda não dá pra calcular teu alvo. Preciso de pesagens e refeições
          registradas — o alvo vem do teu gasto real, não de fórmula.
        </section>
      )}

      <section className="cartao flex flex-col gap-2">
        <span className="rotulo">Gasto estimado (TDEE)</span>
        {tdee.tdee !== null ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums">{tdee.tdee}</span>
              <span className="text-sm text-suave">kcal/dia</span>
              <span
                className={`ml-auto rounded-full px-2 py-0.5 text-xs ${
                  tdee.confidence === "high"
                    ? "bg-bom/15 text-bom"
                    : tdee.confidence === "medium"
                      ? "bg-mostarda/20 text-mostardaEscuro"
                      : "bg-alerta/10 text-alerta"
                }`}
              >
                confiança {{ high: "alta", medium: "média", low: "baixa", none: "nenhuma" }[tdee.confidence]}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-suave">
              Calculado da tua tendência de peso contra o que tu registrou. Vive na
              escala do teu log: se tu subnotifica sempre igual, o alvo sai certo
              mesmo assim.
            </p>
            {tdee.note && <p className="text-xs text-alerta">{tdee.note}</p>}
          </>
        ) : (
          <p className="text-sm text-suave">{tdee.note ?? "Sem dado suficiente ainda."}</p>
        )}
      </section>

      <section className="cartao flex items-center justify-between">
        <div>
          <span className="rotulo">Peso (tendência)</span>
          <p className="text-2xl font-bold tabular-nums">
            {weight.smoothedKg !== null ? `${weight.smoothedKg.toFixed(1)} kg` : "—"}
          </p>
          {weight.trendKgPerWeek !== null && (
            <p className="text-xs text-suave">
              {weight.trendKgPerWeek >= 0 ? "+" : ""}
              {weight.trendKgPerWeek.toFixed(2)} kg/semana
            </p>
          )}
        </div>
        <button className="botao-secundario" onClick={aoAbrirPeso}>
          {weight.todayKg !== null ? "Corrigir" : "Pesar"}
        </button>
      </section>

      {/*
        Convite à importação, e só enquanto ela não aconteceu. Aparece só no
        iPhone porque no Android o dado entra sozinho pelo Health Connect — e um
        botão que leva a um passo a passo do app Saúde, num aparelho que não tem
        app Saúde, é ruído.

        Some assim que houver histórico: é uma tarefa de uma vez só, e um
        chamado permanente para algo já feito é a maneira mais rápida de a
        pessoa parar de ler a tela inteira.
      */}
      {dados.user.platform === "ios" && dados.series.length < 14 && (
        <section className="card">
          <h2 className="text-sm font-medium">Trazer o histórico</h2>
          <p className="mt-1 text-xs text-suave">
            O app Saúde já tem seu peso e seu sono de meses atrás. Sem esse passado, o Mostarda
            leva umas três semanas para ter o que dizer.
          </p>
          <button className="botao-secundario mt-3" onClick={aoAbrirImportacao}>
            Importar do app Saúde
          </button>
        </section>
      )}

      {/*
        Convite ao pareamento, só no Android e só enquanto não houver dado
        passivo. É o espelho do convite de importação do iPhone: cada
        plataforma tem uma ponte só, e mostrar a do outro seria ruído.
      */}
      {dados.user.platform === "android" && dados.series.length < 14 && (
        <section className="card">
          <h2 className="text-sm font-medium">Ligar o relógio</h2>
          <p className="mt-1 text-xs text-suave">
            Instale o app de sincronia e o Health Connect passa a alimentar o Mostarda sozinho —
            sono, passos, treino e frequência cardíaca.
          </p>
          <button className="botao-secundario mt-3" onClick={aoAbrirPareamento}>
            Parear o app Android
          </button>
        </section>
      )}

      <nav className="fixed inset-x-0 bottom-0 border-t border-borda bg-superficie/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
        <div className="mx-auto flex max-w-md gap-2">
          <button className="botao-primario flex-1" onClick={aoAbrirFoto}>
            Fotografar refeição
          </button>
        </div>
      </nav>
    </main>
  );
}
